import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2, RefreshCcw, ShieldCheck } from "lucide-react";
import { formatError } from "@/lib/api";

export default function OtpVerificationPanel({
  otp,
  mobileValid,
  readyForVerification,
  getPayload,
  contextKey,
  contextNotice,
  onRequestStarted,
}) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const getPayloadRef = useRef(getPayload);
  const prepareRef = useRef(otp.prepare);

  useEffect(() => {
    getPayloadRef.current = getPayload;
    prepareRef.current = otp.prepare;
  });

  useEffect(() => {
    setCode("");
    setError("");
  }, [contextKey]);

  useEffect(() => {
    if (!otp.enabled || !mobileValid || otp.canProceed) return undefined;
    const timerId = window.setTimeout(() => {
      prepareRef.current(getPayloadRef.current()).catch((requestError) => {
        setError(requestErrorMessage(requestError, "Verification could not be prepared."));
      });
    }, 350);
    return () => window.clearTimeout(timerId);
  }, [contextKey, mobileValid, otp.enabled, otp.canProceed]);

  if (otp.loading) {
    return (
      <div
        data-testid="otp-config-loading"
        className="mt-3 rounded-2xl border border-line bg-ivory/60 px-4 py-3 text-xs text-muted2"
      >
        Loading mobile verification…
      </div>
    );
  }

  if (otp.configError) {
    return (
      <div
        data-testid="otp-config-error"
        className={
          "mt-3 rounded-2xl border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 "
          + "text-sm text-[#8A2A2A]"
        }
      >
        Mobile verification is temporarily unavailable. Please try again in a little while.
      </div>
    );
  }

  if (!otp.enabled) return null;

  const requestErrorMessage = (requestError, fallback) => {
    const retryAfter = requestError?.response?.headers?.["retry-after"];
    const message = formatError(requestError) || fallback;
    const seconds = Number(retryAfter);
    if (!Number.isFinite(seconds) || seconds <= 0) return message;
    const duration = seconds >= 60
      ? `${Math.ceil(seconds / 60)} minute${seconds >= 120 ? "s" : ""}`
      : `${seconds} seconds`;
    return `${message} Try again in about ${duration}.`;
  };

  const requestCode = async () => {
    setError("");
    if (!otp.prepared) {
      setError("Preparing verification. Please wait a moment.");
      return;
    }
    try {
      const started = await otp.begin(getPayload());
      if (started) {
        setCode("");
        onRequestStarted?.();
      }
    } catch (requestError) {
      setError(
        requestErrorMessage(
          requestError,
          "Verification could not be started. Please try again."
        )
      );
    }
  };

  const verifyCode = async () => {
    setError("");
    try {
      await otp.verifyMockCode(code);
    } catch (requestError) {
      setError(requestErrorMessage(requestError, "The verification code is not correct."));
    }
  };

  if (otp.verified) {
    return (
      <div data-testid="otp-verified-state">
        <div
          data-testid="otp-verified-badge"
          className="mt-3 flex items-center gap-2 rounded-2xl border border-[#BBF7D0] bg-[#F0FDF4] px-4 py-3 text-[#15803D]"
        >
          <CheckCircle2 size={17} strokeWidth={1.9} className="shrink-0" />
          <span className="text-sm font-medium">
            {otp.mode === "preview_mock"
              ? "Preview test verification completed. No SMS was sent."
              : "Mobile number verified successfully."}
          </span>
        </div>
      </div>
    );
  }

  return (
    <section
      data-testid="otp-verification-panel"
      className={
        "mt-3 rounded-2xl border border-maroon/20 bg-gradient-to-br from-white "
        + "to-ivory/60 p-4 sm:p-5"
      }
    >
      <div className="flex gap-3">
        <span
          className={
            "h-9 w-9 shrink-0 rounded-xl bg-maroon/[0.08] text-maroon inline-flex "
            + "items-center justify-center"
          }
        >
          <ShieldCheck size={17} strokeWidth={1.8} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-ink">Mobile Number Verification</p>
          <p className="mt-1 text-xs leading-relaxed text-muted2">
            Our delivery partner will contact you on this number to coordinate your order delivery.
          </p>
        </div>
      </div>

      {contextNotice && (
        <p
          data-testid="otp-context-reset-message"
          className={
            "mt-3 rounded-xl border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 "
            + "text-xs text-[#92400E]"
          }
        >
          {contextNotice}
        </p>
      )}

      {!otp.requested ? (
        <button
          type="button"
          data-testid="send-otp-btn"
          aria-label="Send verification code"
          onClick={requestCode}
          disabled={!mobileValid || !readyForVerification || otp.busy || otp.preparing || !otp.prepared}
          className="btn-maroon mt-4 w-full !px-3 !py-3 !text-sm disabled:cursor-not-allowed disabled:opacity-40"
        >
          {otp.busy || otp.preparing ? (
            <Loader2 className="animate-spin" size={16} />
          ) : (
            <ShieldCheck size={16} />
          )}
          {otp.busy || otp.preparing ? "Preparing…" : "Send OTP"}
        </button>
      ) : otp.mode === "preview_mock" ? (
        <div className="mt-4 space-y-3">
          <p
            data-testid="preview-otp-code-hint"
            className={
              "rounded-xl border border-maroon/15 bg-maroon/[0.04] px-3 py-2 "
              + "text-xs text-maroon"
            }
          >
            Preview test mode: use code 1234. No SMS is sent.
          </p>
          <label className="block text-[11px] uppercase tracking-[0.2em] text-ink">
            Verification code
          </label>
          <input
            data-testid="otp-input"
            value={code}
            onChange={(event) => {
              setCode(event.target.value.replace(/\D/g, "").slice(0, 4));
            }}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={4}
            placeholder="Enter 4-digit code"
            className="luxury-input text-center tracking-[0.45em]"
          />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <button
              type="button"
              data-testid="verify-otp-btn"
              onClick={verifyCode}
              disabled={code.length !== 4 || otp.busy}
              className="btn-maroon w-full disabled:cursor-not-allowed disabled:opacity-40"
            >
              {otp.busy ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <CheckCircle2 size={16} />
              )}
              Verify preview code
            </button>
            <button
              type="button"
              data-testid="resend-otp-btn"
              onClick={requestCode}
              disabled={otp.busy}
              className={
                "btn-outline-maroon w-full inline-flex items-center justify-center gap-2 "
                + "disabled:cursor-not-allowed disabled:opacity-40"
              }
            >
              <RefreshCcw size={15} strokeWidth={1.8} /> Resend code
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {!otp.liveError && (
            <p className="rounded-xl border border-line bg-white px-3 py-2 text-xs text-muted2">
              Complete the verification window to continue.
            </p>
          )}
          {otp.liveError && (
            <p
              data-testid="otp-live-error-message"
              className={
                "rounded-xl border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 "
                + "text-xs text-[#8A2A2A]"
              }
            >
              {otp.liveError}
            </p>
          )}
          {otp.liveError && (
            <button
              type="button"
              data-testid="retry-live-otp-btn"
              onClick={requestCode}
              disabled={otp.busy}
              className={
                "btn-outline-maroon w-full inline-flex items-center justify-center gap-2 "
                + "disabled:cursor-not-allowed disabled:opacity-40"
              }
            >
              <RefreshCcw size={15} strokeWidth={1.8} /> Try again
            </button>
          )}
        </div>
      )}

      {error && (
        <p
          data-testid="otp-error-message"
          className={
            "mt-3 rounded-xl border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 "
            + "text-xs text-[#8A2A2A]"
          }
        >
          {error}
        </p>
      )}
    </section>
  );
}